import {
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { resolveIntegrationConflict, searchIntegrationConflicts } from '../../api/business/endpoints';
import type { ConflictKind, ConflictSortKey, IntegrationLink } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatNumber } from '../../components/format';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

/** What the owner has to look at — server-paginated and server-sorted (Conventions/08 §3). */
export const IntegrationConflictsTable = ({
  link,
  canManage,
}: {
  link: IntegrationLink;
  canManage: boolean;
}) => {
  const { language, t } = useLanguage();
  const [open, setOpen] = useState(true);
  const [kind, setKind] = useState<ConflictKind | ''>('');
  const [page, setPage] = useState(0);
  const sort = useSort<ConflictSortKey>('createdAt', 'desc', () => setPage(0));
  const query = useQuery({
    queryKey: ['integration-conflicts', link.id, open, kind, page, sort.key, sort.direction],
    queryFn: () =>
      searchIntegrationConflicts(link.companyId, link.id, {
        open,
        kind: kind || undefined,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });
  const resolve = useAppMutation(resolveIntegrationConflict, [
    'integration-conflicts',
    'integration-link',
    'integration-links',
  ]);
  const rows = query.data?.items ?? [];

  return (
    <Paper variant="outlined">
      <ErrorNotice error={query.error ?? resolve.error} onRetry={() => void query.refetch()} />
      <TableFilters>
        <TextField
          select
          size="small"
          label={t('conflicts.show')}
          value={open ? 'open' : 'all'}
          onChange={(e) => {
            setOpen(e.target.value === 'open');
            setPage(0);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="open">{t('conflicts.open')}</MenuItem>
          <MenuItem value="all">{t('common.all')}</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label={t('conflicts.kind')}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as ConflictKind | '');
            setPage(0);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {(['OVERSELL', 'UNMAPPED', 'REJECTED', 'UNREACHABLE'] as const).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`conflictKind.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableCell sort={sort} column="createdAt">
                {t('movements.when')}
              </SortableCell>
              <SortableCell sort={sort} column="kind">
                {t('conflicts.kind')}
              </SortableCell>
              <TableCell>{t('conflicts.order')}</TableCell>
              <SortableCell sort={sort} column="quantity" align="right">
                {t('conflicts.quantity')}
              </SortableCell>
              <TableCell>{t('conflicts.detail')}</TableCell>
              <TableCell align="right">{t('conflicts.resolved')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={6} />
            ) : (
              rows.map((conflict) => (
                <TableRow key={conflict.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(conflict.createdAt, language)}
                  </TableCell>
                  <TableCell>{t(`conflictKind.${conflict.kind}`)}</TableCell>
                  <TableCell>{conflict.orderReference ?? t('common.none')}</TableCell>
                  <TableCell align="right">
                    {conflict.quantity === null
                      ? t('common.none')
                      : formatNumber(conflict.quantity, language)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Typography variant="body2">{conflict.detail}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {conflict.resolvedAt !== null ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(conflict.resolvedAt, language)}
                        <br />
                        {conflict.resolvedByEmail === 'system'
                          ? t('conflicts.bySystem')
                          : conflict.resolvedByEmail}
                      </Typography>
                    ) : canManage ? (
                      <Button
                        size="small"
                        disabled={resolve.isPending}
                        onClick={() =>
                          resolve.mutate({
                            companyId: link.companyId,
                            linkId: link.id,
                            conflictId: conflict.id,
                          })
                        }
                      >
                        {t('conflicts.resolve')}
                      </Button>
                    ) : (
                      t('common.none')
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? (
        <EmptyState title={t('conflicts.empty')} detail={t('conflicts.emptyDetail')} />
      ) : null}
      <ServerPagination
        page={page}
        pageSize={query.data?.pageSize ?? 25}
        total={query.data?.total ?? 0}
        onPageChange={setPage}
      />
    </Paper>
  );
};
